import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from './cn';
import { useFieldControl } from './Field';
import { controlBox } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import type { Option } from './Select';
import { Spinner } from './Spinner';
import { useListNavigation } from './useListNavigation';

export interface ComboboxProps<V extends string | number> {
  options: readonly Option<V>[];
  value: V | null | undefined;
  /** Called with the picked value, or null for the `clearLabel` entry. */
  onValueChange(value: V | null): void;
  /** Trigger text when nothing is picked. */
  placeholder?: string;
  /** Adds a first entry meaning "none" (e.g. "All my markets"); picking it reports null. */
  clearLabel?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Remote search: called as the user types; `options` are then shown as given (not filtered here).
   * Without it, options are filtered by label and hint, case-insensitively.
   */
  onQueryChange?(query: string): void;
  'aria-label'?: string;
  id?: string;
  className?: string;
}

type Entry<V> = { value: V | null; label: string; hint?: string; disabled?: boolean };

/**
 * Searchable single choice for long lists (markets, vehicles, admins). A button opens a popover
 * with a search box; ↑/↓ move, Enter picks, Esc closes, and focus returns to the trigger.
 */
export function Combobox<V extends string | number>({
  options,
  value,
  onValueChange,
  placeholder,
  clearLabel,
  searchPlaceholder,
  emptyText,
  loading,
  disabled,
  onQueryChange,
  className,
  ...props
}: ComboboxProps<V>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchLabel = searchPlaceholder ?? t('select.search');
  const listId = useId();
  const field = useFieldControl({ id: props.id });
  const selected = options.find((o) => o.value === value);

  const entries = useMemo<Entry<V>[]>(() => {
    const q = query.trim().toLowerCase();
    const matches = onQueryChange || !q ? options : options.filter((o) => `${o.label} ${o.hint ?? ''}`.toLowerCase().includes(q));
    return clearLabel && !q ? [{ value: null, label: clearLabel }, ...matches] : [...matches];
  }, [options, query, clearLabel, onQueryChange]);

  const pick = (entry: Entry<V> | undefined) => {
    if (!entry || entry.disabled) return;
    onValueChange(entry.value);
    setOpen(false);
  };

  const idOf = useCallback((i: number) => `${listId}-${i}`, [listId]);
  const nav = useListNavigation({
    count: entries.length,
    onPick: (i) => pick(entries[i]),
    idOf,
    resetKey: `${open}:${query}`,
    // opening starts on the current value
    initialIndex: query ? 0 : Math.max(0, entries.findIndex((e) => e.value === (value ?? null))),
  });

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next && query) {
      setQuery('');
      onQueryChange?.('');
    }
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        {...field}
        disabled={disabled}
        role="combobox"
        aria-label={props['aria-label']}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        className={cn(
          controlBox,
          'm-0 cursor-pointer justify-between text-left font-sans outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        <span className={cn('truncate', !selected && value != null && 'text-ink-faint', !selected && value == null && !clearLabel && 'text-ink-faint')}>{selected?.label ?? (value == null && clearLabel ? clearLabel : (placeholder ?? t('select.placeholder')))}</span>
        <ChevronsUpDown size={14} aria-hidden className="shrink-0 text-ink-faint" />
      </PopoverTrigger>
      <PopoverContent className="w-[max(var(--radix-popover-trigger-width),16rem)] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2 border-b border-line px-2.5">
          <Search size={14} aria-hidden className="shrink-0 text-ink-faint" />
          <input
            autoFocus
            role="combobox"
            aria-label={searchLabel}
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={nav.activeId}
            value={query}
            placeholder={searchLabel}
            onChange={(e) => {
              setQuery(e.target.value);
              onQueryChange?.(e.target.value);
            }}
            onKeyDown={nav.onKeyDown}
            className="m-0 h-9 w-full border-0 bg-transparent p-0 font-sans text-sm text-ink outline-none placeholder:text-ink-faint pointer-coarse:h-10 pointer-coarse:text-[16px]"
          />
          {loading && <Spinner size={14} />}
        </div>
        <ul id={listId} role="listbox" aria-label={props['aria-label']} className="m-0 max-h-72 list-none overflow-y-auto p-1">
          {entries.length === 0 && <li className="px-2 py-3 text-center text-sm text-ink-muted">{loading ? t('searching') : (emptyText ?? t('select.noMatches'))}</li>}
          {entries.map((entry, i) => {
            const isSelected = (value ?? null) === entry.value;
            return (
              <li
                key={entry.value ?? '__none'}
                id={idOf(i)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={entry.disabled || undefined}
                onPointerMove={() => nav.setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(entry)}
                className={cn(
                  'flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-sm pointer-coarse:h-10',
                  i === nav.active && 'bg-accent-softer',
                  isSelected && 'font-bold',
                  entry.value === null && 'text-ink-muted',
                  entry.disabled && 'pointer-events-none opacity-50',
                )}
              >
                <span className="truncate">{entry.label}</span>
                {entry.hint && <span className="truncate text-xs text-ink-faint">{entry.hint}</span>}
                {isSelected && <Check size={14} aria-hidden className="ml-auto shrink-0 text-accent" />}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
